'use client';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import { Box, CircularProgress, Grid2, useTheme } from '@mui/material';
import {
    TextDisplayMDSemiBold,
    TextMDSemiBold,
    TextSMSemiBold
} from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { SecondaryButtonGrayLG } from '@/components/shared/Button';
import { useFormatting } from '@/utils/formatting';
import { DATE_FORMAT } from '@/lib/constants';
import { useTransactionMonitoringModalStore } from './TransactionMonitoringStore';
import { NormalGrid, HeaderGrid } from '../history/common';
import { useGetBankTransactionRecordDetails } from '@/services/paymentsQuery';
import Chip from '@/components/shared/Chip';
import dayjs from 'dayjs';

const STATUS_TO_CHIP_COLOR = {
    ExistsInBankOnly: 'error',
    ExistsInInstapaidOnly: 'warning',
    MappedAutomatically: 'success',
    MappedManually: 'success',
    Ignored: 'muted',
    TopUpCreated: 'success'
};

export const TransactionMonitoringDetailDialog = () => {
    const isOpen = useTransactionMonitoringModalStore((state) => state.isOpen);
    const closeModal = useTransactionMonitoringModalStore(
        (state) => state.closeModal
    );
    const bankTransactionRecordId = useTransactionMonitoringModalStore(
        (state) => state.bankTransactionRecordId
    );

    const { t } = useTranslation();
    const theme = useTheme();

    const { getBalanceLabel } = useFormatting();

    const { data, isLoading } = useGetBankTransactionRecordDetails(
        bankTransactionRecordId || '',
        isOpen && !!bankTransactionRecordId
    );

    const content = data?.data;

    return (
        <StyledDialog open={isOpen}>
            <StyledDialogContainer
                style={{
                    width: '480px',
                    padding: '24px'
                }}
            >
                {isLoading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="300px"
                    >
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && content && (
                    <>
                        <Box textAlign={'center'}>
                            <TextMDSemiBold mb={1}>
                                {t('Transaction Monitoring Details')}
                            </TextMDSemiBold>
                            <TextDisplayMDSemiBold>
                                {getBalanceLabel(
                                    content?.amount,
                                    content?.currency ?? 'DKK',
                                    undefined,
                                    true
                                )}
                            </TextDisplayMDSemiBold>
                        </Box>

                        <Grid2 mb={3} container spacing={2} mt={2}>
                            <NormalGrid size={6}>
                                <TextSMSemiBold
                                    color={theme.palette.text.muted}
                                >
                                    {t('Date')}
                                </TextSMSemiBold>
                            </NormalGrid>
                            <NormalGrid
                                size={6}
                                display={'flex'}
                                justifyContent={'end'}
                            >
                                <TextSMSemiBold>
                                    {dayjs(content?.date).format(
                                        DATE_FORMAT.FULL_DATE_TIME
                                    )}
                                </TextSMSemiBold>
                            </NormalGrid>

                            <NormalGrid size={6}>
                                <TextSMSemiBold
                                    color={theme.palette.text.muted}
                                >
                                    {t('Status')}
                                </TextSMSemiBold>
                            </NormalGrid>
                            <NormalGrid
                                size={6}
                                display={'flex'}
                                justifyContent={'end'}
                            >
                                <Chip
                                    color={
                                        STATUS_TO_CHIP_COLOR[
                                            content.status as keyof typeof STATUS_TO_CHIP_COLOR
                                        ] as any
                                    }
                                    label={<span>{content.status}</span>}
                                />
                            </NormalGrid>

                            {content?.type && (
                                <>
                                    <NormalGrid size={6}>
                                        <TextSMSemiBold
                                            color={theme.palette.text.muted}
                                        >
                                            {t('Type')}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                    <NormalGrid
                                        size={6}
                                        display={'flex'}
                                        justifyContent={'end'}
                                    >
                                        <TextSMSemiBold fontWeight={700}>
                                            {content?.type}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}

                            {content?.from && (
                                <>
                                    <NormalGrid size={6}>
                                        <TextSMSemiBold
                                            color={theme.palette.text.muted}
                                        >
                                            {t('From')}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                    <NormalGrid
                                        size={6}
                                        display={'flex'}
                                        justifyContent={'end'}
                                    >
                                        <TextSMSemiBold fontWeight={700}>
                                            {content?.from}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}

                            {content?.text && (
                                <>
                                    <NormalGrid size={6}>
                                        <TextSMSemiBold
                                            color={theme.palette.text.muted}
                                        >
                                            {t('Text')}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                    <NormalGrid
                                        size={6}
                                        display={'flex'}
                                        justifyContent={'end'}
                                    >
                                        <TextSMSemiBold>
                                            {content?.text}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}

                            {content?.comment && (
                                <>
                                    <NormalGrid size={6}>
                                        <TextSMSemiBold
                                            color={theme.palette.text.muted}
                                        >
                                            {t('Comment')}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                    <NormalGrid
                                        size={6}
                                        display={'flex'}
                                        justifyContent={'end'}
                                    >
                                        <TextSMSemiBold>
                                            {content?.comment}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}

                            {content?.senderText && (
                                <>
                                    <NormalGrid size={6}>
                                        <TextSMSemiBold
                                            color={theme.palette.text.muted}
                                        >
                                            {t('OriginalText')}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                    <NormalGrid
                                        size={6}
                                        display={'flex'}
                                        justifyContent={'end'}
                                    >
                                        <TextSMSemiBold>
                                            {content?.senderText}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}

                            <HeaderGrid
                                size={12}
                                display={'flex'}
                                justifyContent={'start'}
                            >
                                <TextSMSemiBold>
                                    {t('Transaction IDs')}
                                </TextSMSemiBold>
                            </HeaderGrid>

                            <NormalGrid size={4}>
                                <TextSMSemiBold
                                    color={theme.palette.text.muted}
                                >
                                    {t('End to end ID')}
                                </TextSMSemiBold>
                            </NormalGrid>
                            <NormalGrid p={0} size={8} textAlign={'right'}>
                                <TextSMSemiBold>
                                    {content?.bankTransactionRecordId || '-'}
                                </TextSMSemiBold>
                            </NormalGrid>

                            {content?.aiiaTransactionId && (
                                <>
                                    <NormalGrid size={4}>
                                        <TextSMSemiBold
                                            color={theme.palette.text.muted}
                                        >
                                            {t('Bban')}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                    <NormalGrid
                                        p={0}
                                        size={8}
                                        textAlign={'right'}
                                    >
                                        <TextSMSemiBold>
                                            {content?.aiiaTransactionId}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}

                            {content?.transactionId && (
                                <>
                                    <NormalGrid size={4}>
                                        <TextSMSemiBold
                                            color={theme.palette.text.muted}
                                        >
                                            {t('Transaction ID')}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                    <NormalGrid
                                        p={0}
                                        size={8}
                                        textAlign={'right'}
                                    >
                                        <TextSMSemiBold>
                                            {content?.transactionId}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}

                            {content?.employeeId && (
                                <>
                                    <NormalGrid size={4}>
                                        <TextSMSemiBold
                                            color={theme.palette.text.muted}
                                        >
                                            {t('User/Company ID')}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                    <NormalGrid
                                        p={0}
                                        size={8}
                                        textAlign={'right'}
                                    >
                                        <TextSMSemiBold>
                                            {content?.employeeId}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}

                            {content?.message && (
                                <>
                                    <HeaderGrid
                                        size={12}
                                        display={'flex'}
                                        justifyContent={'start'}
                                    >
                                        <TextSMSemiBold>
                                            {t('Original message')}
                                        </TextSMSemiBold>
                                    </HeaderGrid>
                                    <NormalGrid size={12}>
                                        <TextSMSemiBold>
                                            {content?.message}
                                        </TextSMSemiBold>
                                    </NormalGrid>
                                </>
                            )}
                        </Grid2>

                        <SecondaryButtonGrayLG onClick={closeModal}>
                            {t('close')}
                        </SecondaryButtonGrayLG>
                    </>
                )}
            </StyledDialogContainer>
        </StyledDialog>
    );
};
